<div class="fcomp-shell">

  <!-- Header + score bar -->
  <div class="fcomp-header">
    <div>
      <button type="button" class="fcomp-backlink" (click)="backToList()">
        <i class="pi pi-arrow-left"></i>
        {{ 'FLEXI_COMPLIANCE.LIST.BACK_TO_LIST' | translate }}
      </button>
      <h2 class="fcomp-header__title">{{ 'FLEXI_COMPLIANCE.TITLE' | translate }}</h2>
      <p class="fcomp-header__meta">
        <span>{{ aggregate.catalogVersionName }}</span>
        <span class="fcomp-dot" *ngIf="aggregate.periodFrom && aggregate.periodTo">·</span>
        <span *ngIf="aggregate.periodFrom && aggregate.periodTo">
          {{ aggregate.periodFrom }} – {{ aggregate.periodTo }}
        </span>
      </p>
    </div>
    <div class="fcomp-header__status" [ngClass]="{
      'is-draft': aggregate.status === Status.Draft,
      'is-in-progress': aggregate.status === Status.InProgress,
      'is-completed': aggregate.status === Status.Completed
    }">
      <i class="pi" [ngClass]="{
        'pi-pencil': aggregate.status === Status.Draft,
        'pi-spinner': aggregate.status === Status.InProgress,
        'pi-check-circle': aggregate.status === Status.Completed
      }"></i>
      <span *ngIf="aggregate.status === Status.Draft">{{ 'FLEXI_COMPLIANCE.STATUS.DRAFT' | translate }}</span>
      <span *ngIf="aggregate.status === Status.InProgress">{{ 'FLEXI_COMPLIANCE.STATUS.IN_PROGRESS' | translate }}</span>
      <span *ngIf="aggregate.status === Status.Completed">{{ 'FLEXI_COMPLIANCE.STATUS.COMPLETED' | translate }}</span>
    </div>
  </div>

  <!-- Score bar with smiley -->
  <div class="fcomp-score" [ngClass]="smileyClass()">
    <div class="fcomp-score__face">
      <img [src]="smileyImage()" alt="" class="fcomp-score__face-img"
           [@flexiEmojiEntry]="emojiEntryState" />
    </div>
    <div class="fcomp-score__body">
      <div class="fcomp-score__top">
        <strong>{{ score.percentage }}%</strong>
        <span class="fcomp-score__counts">
          {{ score.greenCount }} {{ 'FLEXI_COMPLIANCE.RATINGS.GOOD' | translate }} ·
          {{ score.yellowCount }} {{ 'FLEXI_COMPLIANCE.RATINGS.NEEDS_IMPROVEMENT' | translate }} ·
          {{ score.redCount }} {{ 'FLEXI_COMPLIANCE.RATINGS.MISSING' | translate }} ·
          {{ score.ratedCount }} / {{ score.totalCount }} {{ 'FLEXI_COMPLIANCE.SCORE.RATED' | translate }}
        </span>
      </div>
      <div class="fcomp-score__bar">
        <div class="fcomp-score__fill" [style.width]="score.percentage + '%'"></div>
      </div>
    </div>
  </div>

  <!-- Banners -->
  <div class="fcomp-banner fcomp-banner--success" *ngIf="isCompleted">
    <i class="pi pi-check-circle"></i>
    <div>
      <strong>{{ 'FLEXI_COMPLIANCE.COMPLETED_BANNER_TITLE' | translate }}</strong>
      {{ 'FLEXI_COMPLIANCE.COMPLETED_BANNER_BODY' | translate }}
    </div>
    <button type="button" class="fcomp-banner__action" (click)="reopen()" [disabled]="saving">
      <i class="pi pi-pencil"></i> {{ 'FLEXI_COMPLIANCE.ACTIONS.REOPEN' | translate }}
    </button>
  </div>

  <!-- Open but every checkpoint already rated: the review is reopened / fully
       rated yet still OPEN. Make that unmistakable so it isn't read as done. -->
  <div class="fcomp-banner fcomp-banner--warning" *ngIf="isOpenAwaitingCompletion">
    <i class="pi pi-exclamation-circle"></i>
    <div>
      <strong>{{ 'FLEXI_COMPLIANCE.OPEN_BANNER_TITLE' | translate }}</strong>
      {{ 'FLEXI_COMPLIANCE.OPEN_BANNER_BODY' | translate }}
    </div>
    <button type="button" class="fcomp-banner__action" (click)="complete()" [disabled]="saving">
      <i class="pi pi-check-circle"></i> {{ 'FLEXI_COMPLIANCE.ACTIONS.COMPLETE' | translate }}
    </button>
  </div>

  <div class="fcomp-banner fcomp-banner--error" *ngIf="errorMessage">
    <i class="pi pi-exclamation-triangle"></i>
    <div>{{ errorMessage }}</div>
    <button type="button" class="fcomp-banner__action" (click)="errorMessage = null">{{ 'FLEXI_COMPLIANCE.DISMISS' | translate }}</button>
  </div>

  <!-- Progress banner while a PDF is generating (Complete or Regenerate) -->
  <div class="fcomp-banner fcomp-banner--info" *ngIf="pdfBusyMessage">
    <i class="pi pi-spin pi-spinner"></i>
    <div>{{ pdfBusyMessage }}</div>
  </div>

  <!-- Transient success banner after Save-to-Documents / Complete -->
  <div class="fcomp-banner fcomp-banner--success" *ngIf="successMessage && !pdfBusyMessage">
    <i class="pi pi-check-circle"></i>
    <div>{{ successMessage }}</div>
    <button type="button" class="fcomp-banner__action" (click)="successMessage = null">{{ 'FLEXI_COMPLIANCE.DISMISS' | translate }}</button>
  </div>

  <!-- Toolbar: show removed toggle + download (when completed) + complete button -->
  <div class="fcomp-toolbar">
    <label class="fcomp-check">
      <input type="checkbox" [(ngModel)]="showRemoved" />
      <span>{{ 'FLEXI_COMPLIANCE.SHOW_REMOVED' | translate }}</span>
    </label>
    <div class="fcomp-toolbar__spacer"></div>
    <button type="button" class="fcomp-btn fcomp-btn--ghost"
            *ngIf="!isCompleted"
            (click)="browseTemplates()">
      <i class="pi pi-list"></i>
      {{ 'FLEXI_COMPLIANCE.ACTIONS.ALL_TEMPLATES' | translate }}
    </button>
    <button type="button" class="fcomp-btn fcomp-btn--outline"
            *ngIf="!isCompleted"
            (click)="editTemplate()"
            [disabled]="ensuringTemplate">
      <i class="pi" [ngClass]="ensuringTemplate ? 'pi-spin pi-spinner' : 'pi-pencil'"></i>
      {{ 'FLEXI_COMPLIANCE.ACTIONS.EDIT_TEMPLATE' | translate }}
    </button>
    <button type="button" class="fcomp-btn fcomp-btn--outline"
            *ngIf="isCompleted"
            (click)="openPdfChooser()"
            [disabled]="downloading || regenerating">
      <i class="pi"
         [ngClass]="(downloading || regenerating) ? 'pi-spin pi-spinner' : 'pi-file-pdf'"></i>
      {{ 'FLEXI_COMPLIANCE.ACTIONS.PDF_OPTIONS' | translate }}
    </button>
    <!-- Once Completed, the way to evolve the assessment is to start a
         fresh one (with the current or a new template). Editing the
         template in place is intentionally hidden after completion: the
         existing assessment's line items are snapshotted and template
         edits silently don't propagate. -->
    <button type="button" class="fcomp-btn fcomp-btn--primary"
            *ngIf="isCompleted"
            (click)="openStartNewReview()">
      <i class="pi pi-refresh"></i>
      {{ 'FLEXI_COMPLIANCE.ACTIONS.START_NEW' | translate }}
    </button>
    <button type="button" class="fcomp-btn fcomp-btn--success"
            *ngIf="!isCompleted"
            (click)="complete()" [disabled]="saving">
      <i class="pi pi-check-circle"></i> {{ 'FLEXI_COMPLIANCE.ACTIONS.COMPLETE' | translate }}
    </button>
  </div>

  <!-- "Previous reviews" strip. Chips are selectable: picking one previews that
       review read-only below, and picking the current one returns here — so
       comparing against last year never means going back to the grid. -->
  <flexi-compliance-history-strip [businessId]="businessId"
                                  [selectedId]="peeked?.id ?? null"
                                  (reviewSelected)="onHistorySelected($event)">
  </flexi-compliance-history-strip>

  <div class="fcomp-peek-loading" *ngIf="peekLoading">
    <i class="pi pi-spin pi-spinner"></i>
    {{ 'FLEXI_COMPLIANCE.LOADING' | translate }}
  </div>

  <!-- Read-only preview of a past review. Rendered by the final view — the same
       component that renders an archived review elsewhere — with its own history
       strip suppressed so the two strips don't stack. -->
  <ng-container *ngIf="peeked">
    <div class="fcomp-banner fcomp-banner--info fcomp-peek-banner">
      <i class="pi pi-history"></i>
      <div class="fcomp-peek-banner__text">
        {{ 'FLEXI_COMPLIANCE.HISTORY.VIEWING_PAST' | translate }}
        <strong *ngIf="peekedOn">{{ peekedOn | date:'mediumDate' }}</strong>
        <small>{{ 'FLEXI_COMPLIANCE.HISTORY.READ_ONLY' | translate }}</small>
      </div>
      <button type="button" class="fcomp-btn fcomp-btn--primary fcomp-btn--sm"
              (click)="backToCurrent()">
        <i class="pi pi-arrow-left"></i>
        {{ 'FLEXI_COMPLIANCE.HISTORY.BACK_TO_CURRENT' | translate }}
      </button>
    </div>

    <flexi-compliance-final-view [aggregate]="peeked" [showHistory]="false">
    </flexi-compliance-final-view>
  </ng-container>

  <!-- Live review: everything below is hidden while previewing a snapshot,
       since none of it can be applied to one. -->
  <ng-container *ngIf="!peeked">

  <!-- Sections — accordion: one open at a time, click to expand/collapse -->
  <section class="fcomp-section" *ngFor="let s of aggregate.sections; let si = index"
           [ngClass]="'tone-' + sectionTone(s)"
           [class.is-open]="isOpen(s.id)">
    <header class="fcomp-section__head" (click)="toggleSection(s.id)" role="button" [attr.aria-expanded]="isOpen(s.id)">
      <div class="fcomp-section__head-left">
        <span class="fcomp-section__num">{{ si + 1 }}</span>
        <div class="fcomp-section__title-block">
          <h4>{{ s.title }}</h4>
          <small class="fcomp-section__progress-bar">
            <span class="fcomp-section__progress-fill" [style.width]="sectionProgressPercent(s) + '%'"></span>
          </small>
        </div>
      </div>
      <div class="fcomp-section__head-right">
        <span class="fcomp-section__pill" [ngClass]="'tone-' + sectionTone(s)"
              *ngIf="sectionTone(s) as tone">
          <i class="pi" [ngClass]="{
              'pi-circle': tone === 'pending',
              'pi-spinner': tone === 'in-progress',
              'pi-times-circle': tone === 'red',
              'pi-exclamation-circle': tone === 'yellow',
              'pi-check-circle': tone === 'green'
            }"></i>
          <span *ngIf="tone === 'pending'">{{ 'FLEXI_COMPLIANCE.SECTION_TONE.PENDING' | translate }}</span>
          <span *ngIf="tone === 'in-progress'">{{ 'FLEXI_COMPLIANCE.SECTION_TONE.IN_PROGRESS' | translate }}</span>
          <span *ngIf="tone === 'red'">{{ 'FLEXI_COMPLIANCE.SECTION_TONE.RED' | translate }}</span>
          <span *ngIf="tone === 'yellow'">{{ 'FLEXI_COMPLIANCE.SECTION_TONE.YELLOW' | translate }}</span>
          <span *ngIf="tone === 'green'">{{ 'FLEXI_COMPLIANCE.SECTION_TONE.GREEN' | translate }}</span>
        </span>
        <span class="fcomp-section__count">
          {{ sectionRatedCount(s) }} / {{ sectionTotalCount(s) }}
        </span>
        <i class="pi fcomp-section__chevron" [ngClass]="isOpen(s.id) ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
      </div>
    </header>

    <div class="fcomp-section__body" *ngIf="isOpen(s.id)">
      <div class="fcomp-item" *ngFor="let item of visibleItems(s); let i = index"
           [class.is-removed]="item.isRemoved">
        <div class="fcomp-item__head">
          <span class="fcomp-item__pos">{{ si + 1 }}.{{ i + 1 }}</span>
          <div class="fcomp-item__body">
            <p class="fcomp-item__question">
              {{ item.question }}
              <small *ngIf="item.sourceType === Source.Custom" class="fcomp-item__custom-tag">
                {{ 'FLEXI_COMPLIANCE.CUSTOM_TAG' | translate }}
              </small>
            </p>
            <small class="fcomp-item__ref" *ngIf="item.regulatoryReference">{{ item.regulatoryReference }}</small>
          </div>
        </div>

        <div class="fcomp-item__row">
          <!-- Rating buttons: red / yellow / green -->
          <div class="fcomp-rate">
            <button type="button" class="fcomp-rate__btn is-red"
                    [class.is-active]="item.rating === Rating.Missing"
                    [disabled]="isCompleted || busyLineItemIds.has(item.id)"
                    (click)="setRating(item, Rating.Missing)"
                    [title]="'FLEXI_COMPLIANCE.RATINGS.MISSING' | translate">
              <i class="pi pi-times"></i>
            </button>
            <button type="button" class="fcomp-rate__btn is-yellow"
                    [class.is-active]="item.rating === Rating.NeedsImprovement"
                    [disabled]="isCompleted || busyLineItemIds.has(item.id)"
                    (click)="setRating(item, Rating.NeedsImprovement)"
                    [title]="'FLEXI_COMPLIANCE.RATINGS.NEEDS_IMPROVEMENT' | translate">
              <i class="pi pi-minus"></i>
            </button>
            <button type="button" class="fcomp-rate__btn is-green"
                    [class.is-active]="item.rating === Rating.Good"
                    [disabled]="isCompleted || busyLineItemIds.has(item.id)"
                    (click)="setRating(item, Rating.Good)"
                    [title]="'FLEXI_COMPLIANCE.RATINGS.GOOD' | translate">
              <i class="pi pi-check"></i>
            </button>
          </div>

          <!-- Comment -->
          <textarea class="fcomp-comment" rows="2"
                    [(ngModel)]="commentDrafts[item.id]"
                    [disabled]="isCompleted"
                    (blur)="saveComment(item)"
                    [placeholder]="'FLEXI_COMPLIANCE.COMMENT_PLACEHOLDER' | translate"></textarea>

          <!-- Remove / restore -->
          <div class="fcomp-item__actions">
            <button type="button" class="fcomp-icon-btn"
                    *ngIf="!item.isRemoved"
                    [disabled]="isCompleted || busyLineItemIds.has(item.id)"
                    (click)="removeItem(item)"
                    [title]="'FLEXI_COMPLIANCE.ACTIONS.REMOVE' | translate">
              <i class="pi pi-trash"></i>
            </button>
            <button type="button" class="fcomp-icon-btn fcomp-icon-btn--restore"
                    *ngIf="item.isRemoved && item.sourceType !== Source.Custom"
                    [disabled]="isCompleted || busyLineItemIds.has(item.id)"
                    (click)="restoreItem(item)"
                    [title]="'FLEXI_COMPLIANCE.ACTIONS.RESTORE' | translate">
              <i class="pi pi-undo"></i>
            </button>
          </div>
        </div>

        <!-- Action points: existing list + inline add affordance -->
        <div class="fcomp-actions-list" *ngIf="item.actionPoints?.length">
          <div class="fcomp-action"
               *ngFor="let action of item.actionPoints"
               [class.is-done]="action.status === ApStatus.Completed">
            <button type="button" class="fcomp-action__toggle"
                    [class.is-done]="action.status === ApStatus.Completed"
                    [disabled]="isCompleted || busyActionIds.has(action.id)"
                    (click)="toggleActionComplete(item, action)"
                    [title]="'FLEXI_COMPLIANCE.ACTION.TOGGLE' | translate">
              <i class="pi" [ngClass]="action.status === ApStatus.Completed ? 'pi-check-circle' : 'pi-circle'"></i>
            </button>
            <div class="fcomp-action__body">
              <!-- Inline edit form — replaces the read-only body while open -->
              <div class="fcomp-action-add__panel" *ngIf="editActionOpen.has(action.id)">
                <input type="text" class="fcomp-input"
                       [(ngModel)]="editActionInput[action.id].title"
                       [placeholder]="'FLEXI_COMPLIANCE.ACTION.TITLE_PLACEHOLDER' | translate" />
                <textarea class="fcomp-comment" rows="2"
                          [(ngModel)]="editActionInput[action.id].description"
                          [placeholder]="'FLEXI_COMPLIANCE.ACTION.DESC_PLACEHOLDER' | translate"></textarea>
                <div class="fcomp-action-add__row">
                  <label class="fcomp-action-add__field">
                    <span>{{ 'FLEXI_COMPLIANCE.RESPONSIBLE.LABEL' | translate }}</span>
                    <select class="fcomp-input fcomp-input--sm" [(ngModel)]="editActionInput[action.id].responsible">
                      <option [ngValue]="Responsible.Customer">{{ 'FLEXI_COMPLIANCE.RESPONSIBLE.CUSTOMER' | translate }}</option>
                      <option [ngValue]="Responsible.Advisor">{{ 'FLEXI_COMPLIANCE.RESPONSIBLE.ADVISOR' | translate }}</option>
                    </select>
                  </label>
                  <label class="fcomp-action-add__field">
                    <span>{{ 'FLEXI_COMPLIANCE.ACTION.DUE' | translate }}</span>
                    <input type="date" class="fcomp-input fcomp-input--sm" [(ngModel)]="editActionInput[action.id].due" />
                  </label>
                  <label class="fcomp-action-add__field">
                    <span>{{ 'FLEXI_COMPLIANCE.RECURRENCE.LABEL' | translate }}</span>
                    <select class="fcomp-input fcomp-input--sm" [(ngModel)]="editActionInput[action.id].recurrence">
                      <option *ngFor="let t of recurrenceTypes" [ngValue]="t">
                        {{ recurrenceTypeKey(t) | translate }}
                      </option>
                    </select>
                  </label>
                </div>
                <div class="fcomp-action-add__foot">
                  <button type="button" class="fcomp-btn fcomp-btn--ghost fcomp-btn--sm"
                          (click)="cancelEditAction(action.id)">
                    {{ 'CANCEL' | translate }}
                  </button>
                  <button type="button" class="fcomp-btn fcomp-btn--primary fcomp-btn--sm"
                          (click)="submitEditAction(action, item)"
                          [disabled]="!editActionInput[action.id]?.title?.trim() || busyActionIds.has(action.id)">
                    {{ 'FLEXI_COMPLIANCE.ACTION.SAVE' | translate }}
                  </button>
                </div>
              </div>

              <ng-container *ngIf="!editActionOpen.has(action.id)">
              <p class="fcomp-action__title">{{ action.title }}</p>
              <p class="fcomp-action__desc" *ngIf="action.description">{{ action.description }}</p>
              <div class="fcomp-action__meta">
                <span class="fcomp-chip">
                  <i class="pi pi-user"></i>
                  {{ responsibleLabelKey(action.responsibleParty) | translate }}{{ responsibleNameSuffix(action) }}
                </span>
                <span class="fcomp-chip" *ngIf="action.dueDate">
                  <i class="pi pi-calendar"></i>
                  {{ 'FLEXI_COMPLIANCE.ACTION.DUE' | translate }}: {{ action.dueDate | date:'dd-MM-yyyy' }}
                </span>
                <span class="fcomp-chip fcomp-chip--muted" *ngIf="action.createdByName">
                  <i class="pi pi-pencil"></i>
                  {{ action.createdByName }}
                </span>
                <span class="fcomp-chip fcomp-chip--done"
                      *ngIf="action.status === ApStatus.Completed && action.completedOn">
                  <i class="pi pi-check-circle"></i>
                  {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COMPLETED_ON' | translate }}
                  {{ action.completedOn | date:'mediumDate' }}
                  <ng-container *ngIf="action.completedByName">
                    · {{ action.completedByName }}
                  </ng-container>
                </span>
              </div>
              </ng-container>
            </div>
            <!-- Edit / delete are offered only while the action point is
                 outstanding: a completed one is a delivered commitment and
                 stays on the record as written. -->
            <button type="button" class="fcomp-icon-btn"
                    *ngIf="isActionOutstanding(action) && !editActionOpen.has(action.id)"
                    [disabled]="busyActionIds.has(action.id)"
                    (click)="openEditAction(action)"
                    [title]="'FLEXI_COMPLIANCE.ACTION.EDIT' | translate">
              <i class="pi pi-pencil"></i>
            </button>
            <button type="button" class="fcomp-icon-btn"
                    *ngIf="isActionOutstanding(action)"
                    [disabled]="busyActionIds.has(action.id)"
                    (click)="deleteAction(item, action)"
                    [title]="'FLEXI_COMPLIANCE.ACTION.DELETE' | translate">
              <i class="pi pi-trash"></i>
            </button>
          </div>
        </div>

        <!-- Add new action point -->
        <div class="fcomp-action-add" *ngIf="!isCompleted && !item.isRemoved">
          <!-- A red or yellow checkpoint owes the action plan a described
               action before the review can be completed. Saying so on the item
               itself is the only place the advisor can act on it; the error at
               Complete time is a last resort, not the notification. -->
          <span class="fcomp-action-required" *ngIf="needsAction(item)">
            <i class="pi pi-exclamation-circle"></i>
            {{ 'FLEXI_COMPLIANCE.ACTIONS.ACTION_REQUIRED_BADGE' | translate }}
          </span>
          <button type="button" class="fcomp-btn fcomp-btn--ghost fcomp-btn--sm"
                  *ngIf="!addActionOpen.has(item.id)"
                  (click)="openAddAction(item)">
            <i class="pi pi-plus"></i>
            {{ 'FLEXI_COMPLIANCE.ACTION.ADD' | translate }}
          </button>

          <div class="fcomp-action-add__panel" *ngIf="addActionOpen.has(item.id)">
            <input type="text" class="fcomp-input"
                   [(ngModel)]="addActionInput[item.id].title"
                   [placeholder]="'FLEXI_COMPLIANCE.ACTION.TITLE_PLACEHOLDER' | translate" />
            <textarea class="fcomp-comment" rows="2"
                      [(ngModel)]="addActionInput[item.id].description"
                      [placeholder]="'FLEXI_COMPLIANCE.ACTION.DESC_PLACEHOLDER' | translate"></textarea>
            <div class="fcomp-action-add__row">
              <label class="fcomp-action-add__field">
                <span>{{ 'FLEXI_COMPLIANCE.RESPONSIBLE.LABEL' | translate }}</span>
                <select class="fcomp-input fcomp-input--sm" [(ngModel)]="addActionInput[item.id].responsible">
                  <option [ngValue]="Responsible.Customer">{{ 'FLEXI_COMPLIANCE.RESPONSIBLE.CUSTOMER' | translate }}</option>
                  <option [ngValue]="Responsible.Advisor">{{ 'FLEXI_COMPLIANCE.RESPONSIBLE.ADVISOR' | translate }}</option>
                </select>
              </label>
              <label class="fcomp-action-add__field">
                <span>{{ 'FLEXI_COMPLIANCE.ACTION.DUE' | translate }}</span>
                <input type="date" class="fcomp-input fcomp-input--sm" [(ngModel)]="addActionInput[item.id].due" />
              </label>
              <label class="fcomp-action-add__field">
                <span>{{ 'FLEXI_COMPLIANCE.RECURRENCE.LABEL' | translate }}</span>
                <select class="fcomp-input fcomp-input--sm" [(ngModel)]="addActionInput[item.id].recurrence">
                  <option *ngFor="let t of recurrenceTypes" [ngValue]="t">
                    {{ recurrenceTypeKey(t) | translate }}
                  </option>
                </select>
              </label>
            </div>
            <div class="fcomp-action-add__foot">
              <button type="button" class="fcomp-btn fcomp-btn--ghost fcomp-btn--sm" (click)="cancelAddAction(item.id)">
                {{ 'CANCEL' | translate }}
              </button>
              <button type="button" class="fcomp-btn fcomp-btn--primary fcomp-btn--sm"
                      (click)="submitAddAction(item)"
                      [disabled]="!addActionInput[item.id]?.title?.trim() || saving">
                <i class="pi pi-check"></i> {{ 'FLEXI_COMPLIANCE.ACTION.ADD_SUBMIT' | translate }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Add custom item -->
      <div class="fcomp-add" *ngIf="!isCompleted">
        <button type="button" class="fcomp-btn fcomp-btn--outline fcomp-btn--sm"
                *ngIf="!addCustomOpen.has(s.id)"
                (click)="openAddCustom(s.id)">
          <i class="pi pi-plus"></i> {{ 'FLEXI_COMPLIANCE.ACTIONS.ADD_CUSTOM' | translate }}
        </button>

        <div class="fcomp-add__panel" *ngIf="addCustomOpen.has(s.id)">
          <input type="text" class="fcomp-input"
                 [(ngModel)]="addCustomInput[s.id].question"
                 [placeholder]="'FLEXI_COMPLIANCE.ADD.QUESTION_PLACEHOLDER' | translate" />
          <input type="text" class="fcomp-input"
                 [(ngModel)]="addCustomInput[s.id].ref"
                 [placeholder]="'FLEXI_COMPLIANCE.ADD.REF_PLACEHOLDER' | translate" />
          <div class="fcomp-add__foot">
            <button type="button" class="fcomp-btn fcomp-btn--ghost" (click)="cancelAddCustom(s.id)">
              {{ 'CANCEL' | translate }}
            </button>
            <button type="button" class="fcomp-btn fcomp-btn--primary fcomp-btn--sm"
                    (click)="submitAddCustom(s.id)"
                    [disabled]="!addCustomInput[s.id]?.question?.trim() || saving">
              <i class="pi pi-check"></i> {{ 'FLEXI_COMPLIANCE.ACTIONS.ADD' | translate }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ===== Follow-up actions (loose action points) ===== -->
  <section class="fcomp-card fcomp-loose-panel">
    <header class="fcomp-loose-panel__head">
      <h3>{{ 'FLEXI_COMPLIANCE.LOOSE_ACTIONS.TITLE' | translate }}</h3>
      <button type="button"
              class="fcomp-btn fcomp-btn--primary fcomp-btn--sm"
              *ngIf="!looseAddOpen && !isCompleted"
              (click)="openLooseAdd()">
        <i class="pi pi-plus"></i>
        {{ 'FLEXI_COMPLIANCE.LOOSE_ACTIONS.ADD' | translate }}
      </button>
    </header>

    <p class="fcomp-muted" *ngIf="!looseLoading && looseActions.length === 0 && !looseAddOpen">
      {{ 'FLEXI_COMPLIANCE.LOOSE_ACTIONS.EMPTY' | translate }}
    </p>
    <div class="alert alert-danger" *ngIf="looseError">{{ looseError }}</div>

    <!-- Add form -->
    <div class="fcomp-add__panel" *ngIf="looseAddOpen">
      <input type="text" class="fcomp-input"
             [(ngModel)]="looseAddInput.title"
             [placeholder]="'FLEXI_COMPLIANCE.ACTION.TITLE_PLACEHOLDER' | translate" />
      <textarea class="fcomp-input" rows="2"
                [(ngModel)]="looseAddInput.description"
                [placeholder]="'FLEXI_COMPLIANCE.ACTION.DESC_PLACEHOLDER' | translate"></textarea>
      <div class="fcomp-add__row">
        <label>
          <span>{{ 'FLEXI_COMPLIANCE.RESPONSIBLE.LABEL' | translate }}</span>
          <select [(ngModel)]="looseAddInput.responsible">
            <option [ngValue]="Responsible.Customer">{{ 'FLEXI_COMPLIANCE.RESPONSIBLE.CUSTOMER' | translate }}</option>
            <option [ngValue]="Responsible.Advisor">{{ 'FLEXI_COMPLIANCE.RESPONSIBLE.ADVISOR' | translate }}</option>
          </select>
        </label>
        <label>
          <span>{{ 'FLEXI_COMPLIANCE.DEADLINE' | translate }}</span>
          <input type="date" [(ngModel)]="looseAddInput.due" />
        </label>
        <label>
          <span>{{ 'FLEXI_COMPLIANCE.RECURRENCE.LABEL' | translate }}</span>
          <select [(ngModel)]="looseAddInput.recurrence">
            <option *ngFor="let t of recurrenceTypes" [ngValue]="t">
              {{ recurrenceTypeKey(t) | translate }}
            </option>
          </select>
        </label>
      </div>
      <div class="fcomp-add__foot">
        <button type="button" class="fcomp-btn fcomp-btn--ghost" (click)="cancelLooseAdd()">
          {{ 'CANCEL' | translate }}
        </button>
        <button type="button" class="fcomp-btn fcomp-btn--primary fcomp-btn--sm"
                (click)="submitLooseAdd()"
                [disabled]="!looseAddInput.title?.trim() || saving">
          <i class="pi pi-check"></i> {{ 'FLEXI_COMPLIANCE.ACTION.ADD_SUBMIT' | translate }}
        </button>
      </div>
    </div>

    <!-- Loose action list -->
    <ul class="fcomp-action-list" *ngIf="looseActions.length > 0">
      <li *ngFor="let action of looseActions"
          [class.is-completed]="action.status === ApStatus.Completed">
        <button type="button"
                class="fcomp-action__toggle"
                (click)="toggleLooseComplete(action)"
                [disabled]="isCompleted || busyActionIds.has(action.id)"
                [title]="'FLEXI_COMPLIANCE.ACTION.TOGGLE' | translate">
          <i class="pi" [ngClass]="action.status === ApStatus.Completed ? 'pi-check-circle' : 'pi-circle'"></i>
        </button>
        <div class="fcomp-action__body">
          <!-- Inline edit form — replaces the read-only body while open -->
          <div class="fcomp-action-add__panel" *ngIf="editActionOpen.has(action.id)">
            <input type="text" class="fcomp-input"
                   [(ngModel)]="editActionInput[action.id].title"
                   [placeholder]="'FLEXI_COMPLIANCE.ACTION.TITLE_PLACEHOLDER' | translate" />
            <textarea class="fcomp-comment" rows="2"
                      [(ngModel)]="editActionInput[action.id].description"
                      [placeholder]="'FLEXI_COMPLIANCE.ACTION.DESC_PLACEHOLDER' | translate"></textarea>
            <div class="fcomp-action-add__row">
              <label class="fcomp-action-add__field">
                <span>{{ 'FLEXI_COMPLIANCE.RESPONSIBLE.LABEL' | translate }}</span>
                <select class="fcomp-input fcomp-input--sm" [(ngModel)]="editActionInput[action.id].responsible">
                  <option [ngValue]="Responsible.Customer">{{ 'FLEXI_COMPLIANCE.RESPONSIBLE.CUSTOMER' | translate }}</option>
                  <option [ngValue]="Responsible.Advisor">{{ 'FLEXI_COMPLIANCE.RESPONSIBLE.ADVISOR' | translate }}</option>
                </select>
              </label>
              <label class="fcomp-action-add__field">
                <span>{{ 'FLEXI_COMPLIANCE.ACTION.DUE' | translate }}</span>
                <input type="date" class="fcomp-input fcomp-input--sm" [(ngModel)]="editActionInput[action.id].due" />
              </label>
              <label class="fcomp-action-add__field">
                <span>{{ 'FLEXI_COMPLIANCE.RECURRENCE.LABEL' | translate }}</span>
                <select class="fcomp-input fcomp-input--sm" [(ngModel)]="editActionInput[action.id].recurrence">
                  <option *ngFor="let t of recurrenceTypes" [ngValue]="t">
                    {{ recurrenceTypeKey(t) | translate }}
                  </option>
                </select>
              </label>
            </div>
            <div class="fcomp-action-add__foot">
              <button type="button" class="fcomp-btn fcomp-btn--ghost fcomp-btn--sm"
                      (click)="cancelEditAction(action.id)">
                {{ 'CANCEL' | translate }}
              </button>
              <button type="button" class="fcomp-btn fcomp-btn--primary fcomp-btn--sm"
                      (click)="submitEditAction(action)"
                      [disabled]="!editActionInput[action.id]?.title?.trim() || busyActionIds.has(action.id)">
                {{ 'FLEXI_COMPLIANCE.ACTION.SAVE' | translate }}
              </button>
            </div>
          </div>

          <ng-container *ngIf="!editActionOpen.has(action.id)">
          <div class="fcomp-action__title">{{ action.title }}</div>
          <div class="fcomp-action__desc" *ngIf="action.description">{{ action.description }}</div>
          <!-- Chips, matching the per-question list above. These were bare
               <span>s, so the values ran together as one line of plain text. -->
          <div class="fcomp-action__meta">
            <span class="fcomp-chip" *ngIf="action.responsibleParty !== null">
              <i class="pi pi-user"></i>
              {{ (action.responsibleParty === Responsible.Advisor ? 'FLEXI_COMPLIANCE.RESPONSIBLE.ADVISOR' : 'FLEXI_COMPLIANCE.RESPONSIBLE.CUSTOMER') | translate }}{{ responsibleNameSuffix(action) }}
            </span>
            <span class="fcomp-chip" *ngIf="action.dueDate">
              <i class="pi pi-calendar"></i>
              {{ 'FLEXI_COMPLIANCE.ACTION.DUE' | translate }}: {{ action.dueDate | date:'dd-MM-yyyy' }}
            </span>
            <span class="fcomp-chip" *ngIf="action.recurrenceType !== Recurrence.None">
              <i class="pi pi-refresh"></i>
              {{ action.recurrenceName || (recurrenceTypeKey(action.recurrenceType) | translate) }}
            </span>
            <span class="fcomp-chip fcomp-chip--done"
                  *ngIf="action.status === ApStatus.Completed && action.completedOn">
              <i class="pi pi-check-circle"></i>
              {{ 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COMPLETED_ON' | translate }}
              {{ action.completedOn | date:'mediumDate' }}
              <ng-container *ngIf="action.completedByName">
                · {{ action.completedByName }}
              </ng-container>
            </span>
          </div>
          </ng-container>
        </div>
        <!-- Outstanding work only: a completed commitment is neither
             editable nor deletable (deleting the review is the way out). -->
        <button type="button"
                class="fcomp-action__delete"
                *ngIf="isActionOutstanding(action) && !editActionOpen.has(action.id)"
                (click)="openEditAction(action)"
                [disabled]="busyActionIds.has(action.id)"
                [title]="'FLEXI_COMPLIANCE.ACTION.EDIT' | translate">
          <i class="pi pi-pencil"></i>
        </button>
        <button type="button"
                class="fcomp-action__delete"
                *ngIf="isActionOutstanding(action)"
                (click)="deleteLooseAction(action)"
                [disabled]="busyActionIds.has(action.id)"
                [title]="'FLEXI_COMPLIANCE.ACTION.DELETE' | translate">
          <i class="pi pi-trash"></i>
        </button>
      </li>
    </ul>
  </section>

  </ng-container>
</div>

<!-- ===== Start-new-review picker dialog ===== -->
<p-dialog
  [(visible)]="showStartNewDialog"
  [modal]="true"
  [closable]="!startingNewReview"
  [draggable]="false"
  [style]="{ width: '560px', maxWidth: '95vw' }"
  [header]="'FLEXI_COMPLIANCE.START_NEW.TITLE' | translate"
  (onHide)="closeStartNewReview()">

  <p class="fcomp-start-new__intro">
    {{ 'FLEXI_COMPLIANCE.START_NEW.INTRO' | translate }}
  </p>

  <!-- What the sweep will do, before it is agreed to. Shown only when there is
       actually something to lose. -->
  <div class="fcomp-start-new__warning"
       *ngIf="!outstandingLoading && outstandingActionPoints">
    <i class="pi pi-exclamation-triangle"></i>
    <span>{{ 'FLEXI_COMPLIANCE.START_NEW.REJECTS_OUTSTANDING'
              | translate:{ count: outstandingActionPoints } }}</span>
  </div>

  <div class="alert alert-danger" *ngIf="pickerError">{{ pickerError }}</div>

  <!-- Templates list -->
  <div *ngIf="pickerLoading" class="fcomp-start-new__loading">
    <i class="pi pi-spin pi-spinner"></i>
    {{ 'FLEXI_COMPLIANCE.LOADING' | translate }}
  </div>

  <ul class="fcomp-start-new__list" *ngIf="!pickerLoading && pickerTemplates.length > 0">
    <li *ngFor="let t of pickerTemplates"
        class="fcomp-start-new__row"
        [class.is-selected]="selectedTemplateId === t.id"
        (click)="selectedTemplateId = t.id">
      <input type="radio"
             name="newReviewTemplate"
             [value]="t.id"
             [(ngModel)]="selectedTemplateId" />
      <div class="fcomp-start-new__row-body">
        <div class="fcomp-start-new__name">
          {{ t.name }}
          <span class="fcomp-start-new__current" *ngIf="t.isCurrentDefault">
            ({{ 'FLEXI_COMPLIANCE.START_NEW.CURRENT_DEFAULT' | translate }})
          </span>
        </div>
        <div class="fcomp-start-new__desc" *ngIf="t.description">{{ t.description }}</div>
        <div class="fcomp-start-new__catalog">{{ t.catalogVersionCode }}</div>
      </div>
    </li>
  </ul>

  <div *ngIf="!pickerLoading && pickerTemplates.length === 0 && !pickerError"
       class="fcomp-start-new__empty">
    {{ 'FLEXI_COMPLIANCE.START_NEW.NO_TEMPLATES' | translate }}
  </div>

  <ng-template pTemplate="footer">
    <div class="fcomp-start-new__footer">
      <button type="button" class="fcomp-btn fcomp-btn--ghost"
              (click)="goCreateTemplate()"
              [disabled]="startingNewReview">
        <i class="pi pi-plus"></i>
        {{ 'FLEXI_COMPLIANCE.START_NEW.CREATE_NEW' | translate }}
      </button>
      <div class="fcomp-start-new__footer-spacer"></div>
      <button type="button" class="fcomp-btn fcomp-btn--ghost"
              (click)="closeStartNewReview()"
              [disabled]="startingNewReview">
        {{ 'CANCEL' | translate }}
      </button>
      <button type="button" class="fcomp-btn fcomp-btn--primary"
              (click)="confirmStartNewReview()"
              [disabled]="startingNewReview || selectedTemplateId === null">
        <i class="pi" [ngClass]="startingNewReview ? 'pi-spin pi-spinner' : 'pi-refresh'"></i>
        {{ 'FLEXI_COMPLIANCE.START_NEW.START' | translate }}
      </button>
    </div>
  </ng-template>
</p-dialog>

<p-confirmDialog
  [header]="'CONFIRMATION' | translate"
  icon="pi pi-question-circle"
  [style]="{ width: '460px' }"
  [acceptLabel]="'SWITCH_YES' | translate"
  [rejectLabel]="'SWITCH_NO' | translate">
</p-confirmDialog>
